/**
 * O laudo de um mapeamento — por que o arquivo deu no que deu.
 *
 * Vive fora das páginas porque as duas telas que decidem sobre um modelo
 * precisam ver exatamente a mesma leitura: quem cadastra (ModeloNovo) e quem
 * aprova (Modelos). Duas redações diferentes do mesmo resultado seriam um
 * convite a decidir com base em versões distintas do fato.
 *
 * O laudo tem dois autores, e a tela diz qual é qual:
 *
 * - **O sistema** apura. Ele sabe o que leu, o que conferiu e por que falhou —
 *   isso é determinístico, sempre existe, e não depende de serviço externo.
 * - **A IA** redige a partir desses fatos. Ela não descobre a causa: traduz a
 *   que foi apurada. Se a chamada falhar, resta a explicação do sistema, que
 *   já basta.
 *
 * A ordem na tela segue a pergunta de quem lê: *o que aconteceu* → *por que* →
 * *o que eu faço agora* → e só então o detalhe de cada conferência.
 */
import { Badge, Painel } from './common';

/* ── O que aconteceu, por que, e o que fazer ───────────────────────────── */
export function Diagnostico({ diagnostico }) {
  const det = diagnostico?.explicacao;
  const ia = diagnostico?.diagnostico_ia;
  if (!det && !ia) return null;

  const fechou = diagnostico.aprovado;

  return (
    <Painel
      titulo="Diagnóstico"
      subtitulo="O que o sistema fez com este arquivo, e o motivo do resultado."
      acoes={
        <Badge tom={fechou ? 'confirmado' : 'pendente'}>
          {fechou ? 'Conferido' : 'Sem prova aritmética'}
        </Badge>
      }
    >
      <div className="p-4" style={{ fontSize: 14, lineHeight: 1.65 }}>
        {det?.veredito && <p className="mb-3">{det.veredito}</p>}

        {ia?.resumo && (
          <p className="mb-3" style={{ color: 'var(--ff-muted)' }}>
            {ia.resumo}
          </p>
        )}

        {ia?.por_que && (
          <div className="mb-3">
            <div style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
              Por que o resultado foi esse
            </div>
            <p className="mb-0 mt-1">{ia.por_que}</p>
          </div>
        )}

        {(ia?.o_que_fazer || det?.recomendacao) && (
          <div style={{ borderLeft: '3px solid var(--ff-acento)', paddingLeft: 12 }}>
            <div style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>
              O que fazer agora
            </div>
            <p className="mb-0 mt-1">{ia?.o_que_fazer || det.recomendacao}</p>
            {ia?.o_que_fazer && det?.recomendacao && ia.o_que_fazer !== det.recomendacao && (
              <p className="mb-0 mt-2" style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                {det.recomendacao}
              </p>
            )}
          </div>
        )}

        {ia && (
          // Quem lê precisa saber que parte do texto foi redigida por um
          // modelo de linguagem — mesmo que a partir de fatos medidos aqui.
          //
          // O `confiavel_para_baixa` que a IA devolve fica guardado no
          // diagnóstico e não vai para a tela de propósito: quem responde essa
          // pergunta é a conferência aritmética, e dois vereditos lado a lado
          // — um provado, outro opinado — só dariam margem a seguir o errado.
          <p className="mb-0 mt-3" style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
            <i className="bi bi-stars me-1" aria-hidden="true" />
            Texto redigido pela IA a partir dos números que o sistema apurou; ela não refez a
            leitura nem a conferência.
          </p>
        )}
      </div>
    </Painel>
  );
}

/**
 * As conferências, cada uma dizendo o que prova e — quando falha — por quê.
 *
 * O detalhe técnico (`1225 guias sem subtotal`) continua ali: é o que permite
 * a quem entende do motor auditar. Mas ele vem depois da frase em português,
 * não no lugar dela.
 */
export function Conferencias({ diagnostico, comPainel = true }) {
  const validacoes = diagnostico?.validacoes || [];
  const porRegra = Object.fromEntries(
    (diagnostico?.explicacao?.conferencias || []).map((c) => [c.regra, c]),
  );

  const lista =
    validacoes.length === 0 ? (
      <p style={{ fontSize: 14, color: 'var(--ff-muted)', margin: 0 }}>{diagnostico?.mensagem}</p>
    ) : (
      <ul className="list-unstyled m-0">
        {validacoes.map((v) => {
          const extra = porRegra[v.regra] || {};
          return (
            <li key={v.regra} className="d-flex align-items-start gap-2 mb-3">
              <i
                className={`bi ${v.passou ? 'bi-check-circle-fill' : 'bi-x-circle-fill'}`}
                style={{
                  color: v.passou ? '#2fa36c' : '#d92d20',
                  fontSize: 16,
                  marginTop: 2,
                  flexShrink: 0,
                }}
                aria-hidden="true"
              />
              <div>
                <div style={{ fontSize: 14, fontWeight: 500 }}>{v.descricao}</div>

                {extra.o_que_confere && (
                  <div style={{ fontSize: 13, color: 'var(--ff-muted)' }}>
                    {extra.o_que_confere} {extra.por_que_importa}
                  </div>
                )}

                {!v.passou && extra.por_que_falhou && (
                  <div style={{ fontSize: 13, color: '#9a5b06', marginTop: 4 }}>
                    {extra.por_que_falhou}
                  </div>
                )}

                {v.detalhe && (
                  <div className="ff-mono" style={{ fontSize: 12, color: 'var(--ff-muted)' }}>
                    {v.detalhe}
                  </div>
                )}
                {!v.passou && v.esperado != null && (
                  <div className="ff-mono" style={{ fontSize: 12, color: '#9a5b06' }}>
                    esperado {v.esperado} · obtido {v.obtido}
                  </div>
                )}
              </div>
            </li>
          );
        })}
      </ul>
    );

  if (!comPainel) return lista;

  return (
    <Painel
      titulo="Conferência aritmética"
      subtitulo="O modelo só se sustenta se a soma das linhas fechar com os totais impressos no próprio documento."
      acoes={
        <Badge tom={diagnostico?.aprovado ? 'confirmado' : 'reprovado'}>
          {diagnostico?.aprovado ? 'Fechou' : 'Não fechou'}
        </Badge>
      }
    >
      <div className="p-4">{lista}</div>
    </Painel>
  );
}

/* ── O que a IA propôs, para o humano poder discordar ──────────────────── */
export function LeituraDaIA({ diagnostico }) {
  const raciocinio = diagnostico?.raciocinio_da_ia;
  const estrategia = diagnostico?.estrategia_validacao;
  const limitacoes = diagnostico?.limitacoes;
  if (!raciocinio && !estrategia && !limitacoes) return null;

  const conf = diagnostico.confianca_da_ia;
  const tom = conf === 'alta' ? 'confirmado' : conf === 'baixa' ? 'reprovado' : 'pendente';

  const bloco = (titulo, texto, ajuda) =>
    texto ? (
      <div className="mb-4">
        <div style={{ fontWeight: 600, fontSize: 13, letterSpacing: 0.2 }}>{titulo}</div>
        {ajuda && (
          <div style={{ fontSize: 12, color: 'var(--ff-muted)', marginTop: 2 }}>{ajuda}</div>
        )}
        <p className="mb-0 mt-2" style={{ whiteSpace: 'pre-wrap' }}>
          {texto}
        </p>
      </div>
    ) : null;

  return (
    <Painel
      titulo="Como a IA leu o arquivo"
      subtitulo="O mapeamento é auditável de propósito: dá para discordar dele."
      acoes={conf ? <Badge tom={tom}>Confiança {conf}</Badge> : null}
    >
      <div className="p-4" style={{ fontSize: 14, lineHeight: 1.6 }}>
        {bloco('Como identificou cada campo', raciocinio)}
        {bloco(
          'Como propôs conferir a leitura',
          estrategia,
          'A conferência que este arquivo permite — é ela que o motor executa.',
        )}
        {bloco(
          'O que este arquivo não permite conferir',
          limitacoes,
          'Declarado antes de rodar; serve para saber se a falha era esperada.',
        )}
      </div>
    </Painel>
  );
}
